/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import * as Utils from '@contentstack/utils'
import {Stack} from 'contentstack'

type GetEntry = {
    stack: Stack
    contentTypeUid: string
    locale: string
    referenceFieldPath: string[] | undefined
    jsonRtePath: string[] | undefined
    brand?: string
}

type GetEntryByUid = {
    stack: Stack
    contentTypeUid: string
    entryUid: string
    brand?: string
}

type GetEntryByUrl = {
    stack: Stack
    entryUrl: string | undefined
    locale: string
    contentTypeUid: string
    referenceFieldPath: string[] | undefined
    jsonRtePath: string[] | undefined
    brand?: string
}

type GetEntryByPspUrl = {
    stack: Stack
    url: string
    locale: string
    contentTypeUid: string
    brand?: string
}

type GetEntryByTitle = {
    stack: Stack
    title: string | undefined
    locale: string
    contentTypeUid: string
    referenceFieldPath: string[] | undefined
    jsonRtePath: string[] | undefined
    brand?: string
}

type GetEntryByCategorysfcc = {
    stack: Stack
    categoryId: string
    locale: string
    contentTypeUid: string
    referenceFieldPath: string[] | undefined
    jsonRtePath: string[] | undefined
    brand?: string
}

type GetEntryByProductsfcc = {
    stack: Stack
    productId: string
    locale: string
    contentTypeUid: string
    referenceFieldPath: string[] | undefined
    jsonRtePath: string[] | undefined
    brand?: string
}

type GetEntriesFromIds = {
    stack: Stack
    contentTypeUid: string
    locale: string
    uids: string[]
    jsonRtePath: string[] | undefined
}

type Filter = {
    contentstackValue: string
}

type SelectedFilters = {
    [fieldUID: string]: Filter[]
}

type GetEntriesWithFilterParams = {
    stack: Stack
    uids: string[]
    jsonRtePath: string[] | undefined
    contentTypeUid: string
    locale: string
    from: number | undefined
    limit: number | undefined
    selectedFilters: SelectedFilters
    andQueries: object[] | undefined
    brand: string | undefined
}

const renderOption = {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-explicit-any
    span: (node: any, next: any) => next(node.children),
}

/**
 *
 * fetches all the entries from specific content-type
 * @param {* content-type uid} contentTypeUid
 * @param {* reference field name} referenceFieldPath
 * @param {* Json RTE path} jsonRtePath
 *
 */
export const getEntry = ({
    stack,
    contentTypeUid,
    referenceFieldPath,
    jsonRtePath,
    locale,
    brand,
}: GetEntry) => {
    return new Promise((resolve, reject) => {
        const query = stack.ContentType(contentTypeUid).Query()
        if (referenceFieldPath) query.includeReference(referenceFieldPath)
        if (locale) query.includeFallback().language(locale)
        if (brand) query.where('taxonomies.brand', brand)
        query
            .addQuery('include_all', 'true')
            .addQuery('include_all_depth', '5')
            .toJSON()
            .find()
            .then(
                (result) => {
                    jsonRtePath &&
                        Utils.jsonToHTML({
                            entry: result,
                            paths: jsonRtePath,
                            renderOption,
                        })
                    resolve(result[0])
                },
                (error) => {
                    reject(error)
                },
            )
    })
}

/**
 *
 * fetches all the entries from specific content-type
 * @param {* content-type uid} contentTypeUid
 * @param {* reference field name} referenceFieldPath
 * @param {* Json RTE path} jsonRtePath
 *
 */
export const getEntryByUid = ({stack, contentTypeUid, entryUid}: GetEntryByUid) => {
    return new Promise((resolve, reject) => {
        const query = stack.ContentType(contentTypeUid).Entry(entryUid)

        query
            .addQuery('include_all', 'true')
            .addQuery('include_all_depth', '5')
            .toJSON()
            .fetch()
            .then(
                (result) => {
                    resolve(result)
                },
                (error) => {
                    reject(error)
                },
            )
    })
}

/**
 *fetches specific entry from a content-type
 *
 * @param {* content-type uid} contentTypeUid
 * @param {* url for entry to be fetched} entryUrl
 * @param {* reference field name} referenceFieldPath
 * @param {* Json RTE path} jsonRtePath
 * @returns
 */
export const getEntryByUrl = ({
    stack,
    contentTypeUid,
    entryUrl,
    referenceFieldPath,
    jsonRtePath,
    locale,
    brand,
}: GetEntryByUrl) => {
    return new Promise((resolve, reject) => {
        const query = stack.ContentType(contentTypeUid).Query()
        if (referenceFieldPath) query.includeReference(referenceFieldPath)
        if (locale) query.includeFallback().language(locale)
        if (brand) query.where('taxonomies.brand', brand)

        query.addQuery('include_all', 'true').addQuery('include_all_depth', '5').toJSON()
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        const data = query.where('url', `${entryUrl}`).find()
        data.then(
            (result) => {
                jsonRtePath &&
                    Utils.jsonToHTML({
                        entry: result,
                        paths: jsonRtePath,
                        renderOption,
                    })
                resolve(result[0])
            },
            (error) => {
                console.error(error)
                reject(error)
            },
        )
    })
}

/**
 *fetches specific entry from a content-type
 *
 * @param {* content-type uid} contentTypeUid
 * @param {* title for entry to be fetched} entryUrl
 * @param {* reference field name} referenceFieldPath
 * @param {* Json RTE path} jsonRtePath
 * @returns
 */
export const getEntryByTitle = ({
    stack,
    contentTypeUid,
    title,
    referenceFieldPath,
    jsonRtePath,
    locale = 'en',
    brand,
}: GetEntryByTitle) => {
    return new Promise((resolve, reject) => {
        const blogQuery = stack.ContentType(contentTypeUid).Query()
        if (referenceFieldPath) blogQuery.includeReference(referenceFieldPath)
        if (brand) blogQuery.where('taxonomies.brand', brand)

        blogQuery.toJSON()

        const data = blogQuery
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            .where('title', `${title}`)
            .includeFallback()
            .language(locale)
            .find()

        data.then(
            (result) => {
                jsonRtePath &&
                    Utils.jsonToHTML({
                        entry: result,
                        paths: jsonRtePath,
                        renderOption,
                    })
                resolve(result[0])
            },
            (error) => {
                console.error(error)
                reject(error)
            },
        )
    })
}

/**
 *
 * fetches all the entries from specific content-type
 * @param {* content-type uid} contentTypeUid
 * @param {* reference field name} referenceFieldPath
 * @param {* Json RTE path} jsonRtePath
 *
 */
export const getEntryByCategorysfcc = ({
    stack,
    categoryId,
    contentTypeUid,
    jsonRtePath,
    locale,
    brand,
}: GetEntryByCategorysfcc) => {
    return new Promise((resolve, reject) => {
        const query = stack.ContentType(contentTypeUid).Query()
        if (brand) query.where('taxonomies.brand', brand)

        query
            .where('categorysfcc.data.id', categoryId)
            .includeFallback()
            .language(locale)
            .addQuery('include_all', 'true')
            .addQuery('include_all_depth', '5')
            .toJSON()
            .find()
            .then(
                (result) => {
                    jsonRtePath &&
                        Utils.jsonToHTML({
                            entry: result,
                            paths: jsonRtePath,
                            renderOption,
                        })
                    resolve(result[0])
                },
                (error) => {
                    reject(error)
                },
            )
    })
}

export const getPageByPspUrl = ({stack, contentTypeUid, url, locale, brand}: GetEntryByPspUrl) => {
    return new Promise((resolve, reject) => {
        // is the same as
        // {"psp_url":{"$in_query":{"url":"test"}}}
        const pspUrlQuery = stack.ContentType('psp_url').Query().where('url', url)
        if (brand) pspUrlQuery.where('taxonomies.brand', brand)

        const query = stack.ContentType(contentTypeUid).Query()
        if (brand) query.where('taxonomies.brand', brand)

        query
            .addQuery('include_all', 'true')
            .addQuery('include_all_depth', '5')
            .includeFallback()
            .language(locale)
            .referenceIn('psp_url', pspUrlQuery)
            .toJSON()
            .find()
            .then(
                (result) => {
                    resolve(result[0])
                },
                (error) => {
                    reject(error)
                },
            )
    })
}

/**
 *
 * fetches all the entries from specific content-type
 * @param {* content-type uid} contentTypeUid
 * @param {* reference field name} referenceFieldPath
 * @param {* Json RTE path} jsonRtePath
 *
 */
export const getEntryByProductsfcc = ({
    stack,
    productId,
    contentTypeUid,
    jsonRtePath,
    locale,
    brand,
}: GetEntryByProductsfcc) => {
    return new Promise((resolve, reject) => {
        const query = stack.ContentType(contentTypeUid).Query()
        if (brand) query.where('taxonomies.brand', brand)
        query
            .where('productsfcc.data.id', productId)
            .includeFallback()
            .language(locale)
            .addQuery('include_all', 'true')
            .addQuery('include_all_depth', '5')
            .toJSON()
            .find()
            .then(
                (result) => {
                    jsonRtePath &&
                        Utils.jsonToHTML({
                            entry: result,
                            paths: jsonRtePath,
                            renderOption,
                        })
                    resolve(result[0])
                },
                (error) => {
                    reject(error)
                },
            )
    })
}

export const getEntriesByIds = ({
    stack,
    uids,
    jsonRtePath,
    contentTypeUid,
    locale,
}: GetEntriesFromIds) => {
    return new Promise((resolve, reject) => {
        const query = stack.ContentType(contentTypeUid).Query()

        query
            .containedIn('uid', uids)
            .includeFallback()
            .language(locale)
            .addQuery('include_all', 'true')
            .addQuery('include_all_depth', '5')
            .toJSON()
            .find()
            .then(
                ([entries]) => {
                    if (entries.length > 0) {
                        if (jsonRtePath) {
                            Utils.jsonToHTML({
                                entry: entries[0],
                                paths: jsonRtePath,
                                renderOption,
                            })
                        }
                        resolve(entries)
                    } else {
                        reject(new Error('No entries found with the specified UIDs'))
                    }
                },
                (error) => reject(error),
            )
    })
}

export const getEntriesWithFilterParams = ({
    stack,
    contentTypeUid,
    locale,
    jsonRtePath,
    from,
    limit,
    selectedFilters,
    brand,
}: GetEntriesWithFilterParams) => {
    return new Promise((resolve, reject) => {
        const baseQuery = stack.ContentType(contentTypeUid).Query()

        if (brand) {
            baseQuery.where('taxonomies.brand', brand)
        }
        if (from) baseQuery.skip(from)
        if (limit) baseQuery.limit(limit)

        if (Object.keys(selectedFilters).length > 0) {
            Object.entries(selectedFilters).forEach(([filterKey, filterValues]) => {
                if (filterValues.length > 0 && filterKey !== 'product_family') {
                    const fieldPath = `recipe_classification.${filterKey}`
                    const titles = filterValues.map((val) => val?.contentstackValue)
                    const refQuery = stack
                        .ContentType(filterKey)
                        .Query()
                        .containedIn('title', titles)
                    baseQuery.referenceIn(fieldPath, refQuery)
                } else if (filterValues.length > 0 && filterKey === 'product_family') {
                    const fieldPath = 'products_utensils.product_model'

                    const titles = filterValues.map((val) => val?.contentstackValue)
                    const productFamilyQuery = stack
                        .ContentType('product_family')
                        .Query()
                        .containedIn('title', titles)

                    const productSeriesQuery = stack
                        .ContentType('product_series')
                        .Query()
                        .referenceIn('product_family', productFamilyQuery)

                    const productModelQuery = stack
                        .ContentType('product_model')
                        .Query()
                        .referenceIn('product_series', productSeriesQuery)

                    baseQuery.referenceIn(fieldPath, productModelQuery)
                }
            })
        }

        baseQuery
            .addQuery('include_all', 'true')
            .addQuery('include_all_depth', '5')
            .language(locale)
            .toJSON()
            .find()
            .then(
                ([entries]) => {
                    if (entries.length > 0) {
                        if (jsonRtePath) {
                            Utils.jsonToHTML({
                                entry: entries[0],
                                paths: jsonRtePath,
                                renderOption,
                            })
                        }
                        resolve(entries)
                    } else {
                        reject(new Error('No entries found for this query'))
                    }
                },
                (error) => reject(error),
            )
    })
}
